/**
 * A small timestamped cache in localStorage, for answers worth keeping
 * between visits but not worth storing on the server (e.g. a parcel's
 * tracking steps). Every entry remembers when it was saved, so the UI can
 * say how old it is. All of a namespace's entries live under one key, and
 * the oldest are dropped past `maxEntries`, so it can't grow without limit.
 *
 * Storage failures (private browsing, a full quota) are swallowed: a cache
 * that can't save is just a cache miss, never a broken page.
 */
export interface CachedEntry<T> {
    data: T;
    /** When it was saved (ms since epoch). */
    savedAt: number;
}

export interface LocalCache<T> {
    read(key: string): CachedEntry<T> | null;
    write(key: string, data: T): CachedEntry<T>;
    remove(key: string): void;
}

export function createLocalCache<T>(namespace: string, { maxEntries = 100 } = {}): LocalCache<T> {
    const load = (): Record<string, CachedEntry<T>> => {
        try {
            const raw = localStorage.getItem(namespace);
            const parsed = raw ? JSON.parse(raw) : {};
            return parsed && typeof parsed === "object" ? parsed : {};
        } catch {
            return {};
        }
    };

    const store = (entries: Record<string, CachedEntry<T>>) => {
        try {
            localStorage.setItem(namespace, JSON.stringify(entries));
        } catch {
            // Non-critical — the cache simply doesn't persist.
        }
    };

    return {
        read(key) {
            const entry = load()[key];
            return entry && typeof entry.savedAt === "number" ? entry : null;
        },
        write(key, data) {
            const entry: CachedEntry<T> = { data, savedAt: Date.now() };
            const entries = { ...load(), [key]: entry };
            const keys = Object.keys(entries);
            if (keys.length > maxEntries) {
                keys.sort((a, b) => entries[a].savedAt - entries[b].savedAt)
                    .slice(0, keys.length - maxEntries)
                    .forEach((oldest) => delete entries[oldest]);
            }
            store(entries);
            return entry;
        },
        remove(key) {
            const entries = load();
            if (key in entries) {
                delete entries[key];
                store(entries);
            }
        },
    };
}
